import { useState, useCallback } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import TeamRadio from "./TeamRadio";
import LocationRadio from "./LocationRadio";
import StreamCheckboxes from "./StreamCheckboxes";
import PrivacyPolicy from "./PrivacyPolicy";
import { ExternalLink } from "src/library/components/exports";

import { initialFormData, initialFormErrors } from "src/library/store/initialState";
import { ACTIONS, useHackathonContext } from "src/library/store/GlobalStateProvider";

import { registerUser } from "src/library/api/api";
import { errorsInPayload } from "./registerUtils";
import { showErrorModal, showSuccessModal } from "../modals/ConfirmModal";
import {
  ConfirmModalProps,
  EntryType,
  RegisterPayload,
  RegisterUserResponse,
  RegisterFormErrors,
  RegisterResponses,
  ModalMessages,
} from "src/library/types/types";
import {
  customRadioCheckboxStyle,
  customButtonStyle,
  zIndexes,
} from "src/library/styles/theme";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function RegisterForm(props: ConfirmModalProps) {
  const { openConfirmModal } = props;
  const [formErrors, setFormErrors] = useState(initialFormErrors);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { state, dispatch } = useHackathonContext();
  const { formData } = state;

  const setGlobalState = useCallback(
    (data: Partial<RegisterPayload>) => {
      dispatch({ type: ACTIONS.UPDATE_FORM_DATA, payload: data });
    },
    [dispatch]
  );

  const handleRadioChange = (name: string, newVal: number) => {
    if (name === "entryType" && newVal === EntryType.Individual) {
      setGlobalState({ [name]: newVal, teamName: initialFormData.teamName });
    } else {
      setGlobalState({ [name]: newVal });
    }
  };

  const handleCheckboxChange = (name: string, newVal: boolean) => {
    setGlobalState({ [name]: newVal });
  };

  const handleStreamChange = (newVal: number) => {
    let tempArr = formData.stream ?? [];
    tempArr = tempArr.includes(newVal)
      ? tempArr.filter((e: number) => e !== newVal)
      : [...tempArr, newVal].filter(Boolean);

    setGlobalState({ ...state, stream: tempArr.sort((a, b) => a - b) });
  };

  const handleFormChange = (event: any) => {
    setGlobalState({ [event.currentTarget.name]: event.currentTarget.value });
  };

  const validatePayload = (initial: RegisterFormErrors, payload: RegisterPayload) => {
    const errorsInForm = errorsInPayload(initial, payload);
    if (Object.values(errorsInForm).some((e) => e !== "")) {
      setFormErrors({ ...errorsInForm });
      return false;
    }
    // reset form errors and let validatePayloadAndSubmit continue
    setFormErrors({ ...initialFormErrors });
    return true;
  };

  const validatePayloadAndSubmit = (captchaToken: string) => {
    const payload = { ...formData, recaptchaResponse: captchaToken } as RegisterPayload;
    if (!validatePayload(initialFormErrors, payload)) {
      throw Error(RegisterResponses.FormError);
    }

    if (!formData.agreeReceiveEmail) {
      throw Error(RegisterResponses.PrivacyError);
    }
    return registerUser(payload);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    // Check Google ReCAPTCHA
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(`${process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}`, {
          action: "submit",
        })
        .then(async (token: string) => await validatePayloadAndSubmit(token))
        .then(async (res: any) => {
          let resp: RegisterUserResponse;
          try {
            resp = await res.json();
          } catch (error) {
            console.error(`Status: ${res.status}, Msg: Unable to parse response.`);
            // Throwing a default error as showErrorModal handles for generic errors
            throw Error();
          }

          if (resp.error) {
            throw Error(resp.error);
          } else if (resp.msg === RegisterResponses.Success) {
            showSuccessModal(openConfirmModal, ModalMessages.Success);
            // RESET EMPTY FORM
            setGlobalState({ ...initialFormData });
            setLoading(false);
          } else {
            throw Error();
          }
        })
        .catch((error: Error) => {
          showErrorModal(openConfirmModal, error);
          setLoading(false);
          return;
        });
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{ position: "relative", zIndex: zIndexes.Above }}
      >
        <Grid container spacing={2} maxWidth="sm" sx={{ mx: isMobile ? "none" : "auto" }}>
          <Grid item xs={12}>
            <TextField
              value={formData.name || initialFormData.name}
              onChange={handleFormChange}
              name="name"
              required
              fullWidth
              id="name"
              label="Full Name"
              autoFocus
              autoComplete="name"
              inputProps={{ maxLength: 200 }}
              helperText={formErrors.name}
              error={Boolean(formErrors.name)}
              variant="filled"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              value={formData.email || initialFormData.email}
              onChange={handleFormChange}
              id="email"
              name="email"
              required
              fullWidth
              label="Email Address"
              autoComplete="email"
              inputProps={{ maxLength: 200 }}
              helperText={formErrors.email}
              error={Boolean(formErrors.email)}
              variant="filled"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              value={formData.organisation || initialFormData.organisation}
              onChange={handleFormChange}
              name="organisation"
              fullWidth
              required
              id="organisation"
              label="Education Institution"
              variant="filled"
              autoComplete="organization"
              inputProps={{ maxLength: 200 }}
              helperText={formErrors.organisation}
              error={Boolean(formErrors.organisation)}
            />
          </Grid>
          {/* <Grid item xs={12}>
          <TextField
            value={formData.phone || initialFormData.phone}
            onChange={handleFormChange}
            required
            fullWidth
            name="phone"
            label="Mobile"
            id="phone"
            inputProps={{ maxLength: 10 }}
            autoComplete="tel"
            helperText={formErrors.phone}
            error={Boolean(formErrors.phone)}
            variant="filled"
          />
        </Grid> */}
          <Grid item xs={12}>
            <Grid container direction="column">
              <Grid item>
                <LocationRadio
                  location={formData.location || initialFormData.location}
                  handleRadioChange={handleRadioChange}
                  style={customRadioCheckboxStyle}
                />
              </Grid>
              {formErrors.location && (
                <Grid item>
                  <Typography
                    variant="caption"
                    component="p"
                    color="#ff6a6a"
                    letterSpacing={0.8}
                    fontSize="0.8em"
                    textAlign="left"
                    sx={{ pl: 2 }}
                  >
                    {formErrors.location}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <StreamCheckboxes
              streams={formData.stream || initialFormData.stream}
              handleStreamChange={handleStreamChange}
              style={customRadioCheckboxStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="column">
              <Grid item>
                <TeamRadio
                  entryType={formData.entryType || initialFormData.entryType}
                  handleRadioChange={handleRadioChange}
                  style={customRadioCheckboxStyle}
                />
              </Grid>
              <Grid item>
                {formErrors.entryType && (
                  <Typography
                    variant="caption"
                    color="error.contrastText"
                    component="p"
                    letterSpacing={1.05}
                    textAlign="left"
                    fontSize="0.8rem"
                    sx={{ px: 1, backgroundColor: "error.main" }}
                  >
                    {formErrors.entryType}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
          {/* {formData.entryType === EntryType.Team && (
          <Grid item xs={12}>
            <TextField
              value={formData.teamName}
              onChange={handleFormChange}
              name="teamName"
              fullWidth
              id="teamName"
              label="Team Name"
              variant="filled"
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
        )} */}
          {/* <Grid item xs={12}>
          <TextField
            value={formData.comment}
            onChange={handleFormChange}
            name="comment"
            fullWidth
            multiline
            rows={4}
            id="comment"
            label="Additional Comments"
            variant="filled"
            inputProps={{ maxLength: 500 }}
          />
        </Grid> */}
          {/* PRIVACY POLICY */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <PrivacyPolicy
              value={formData.agreeReceiveEmail || initialFormData.agreeReceiveEmail}
              style={customRadioCheckboxStyle}
              handleCheckboxChange={handleCheckboxChange}
            />
          </Grid>
          {/* GOOGLE RECAPTCHA POLICY */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography textAlign="left" sx={{ pl: 2 }}>
              This site is protected by reCAPTCHA and the Google{" "}
              <ExternalLink
                link="https://policies.google.com/privacy"
                text="Privacy Policy"
                style={{ color: "#0CD6FF" }}
              />{" "}
              and{" "}
              <ExternalLink
                link="https://policies.google.com/terms"
                text="Terms of Service"
                style={{ color: "#0CD6FF" }}
              />{" "}
              apply.
            </Typography>
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={loading || !state.registrationsAvailable}
                sx={customButtonStyle("large")}
                component="button"
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
